import React from 'react';
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import { ChevronDownIcon, ChevronUpIcon } from '../../assets';
import { breakpointMap, theme } from '../../theme';
import { BodyText, Heading, LinkText } from '../typography';
import { BaseAccordion } from './BaseAccordion';
import { AccordionSize } from './types';

export type AccordionQAndAProps = {
  answer: string;
  href: string;
  open: boolean;
  size: AccordionSize;
  title: string;
  toggleOpen: () => void;
};

type sizeStyle = {
  header: FlattenSimpleInterpolation;
  title: FlattenSimpleInterpolation;
  icon: FlattenSimpleInterpolation;
  body: FlattenSimpleInterpolation;
  content: FlattenSimpleInterpolation;
};

const smallStyles: sizeStyle = {
  header: css`
    padding: ${theme.spacings[2].rem} ${theme.spacings[4].rem};
  `,
  title: css`
    ${theme.typography.headings[4]['sm-lg']}
  `,
  icon: css`
    width: 18px;
  `,
  body: css`
    padding: 0 ${theme.spacings[4].rem};
  `,
  content: css`
    padding: 0 0 0 ${theme.spacings[4].rem};
  `,
};

const mediumStyles: sizeStyle = {
  header: css`
    padding: ${theme.spacings[4].rem} ${theme.spacings[5].rem};
  `,
  title: smallStyles.title,
  icon: smallStyles.icon,
  body: css`
    padding: 0 ${theme.spacings[5].rem};
  `,
  content: css`
    padding: 0 0 0 ${theme.spacings[5].rem};
  `,
};

const largeStyles: sizeStyle = {
  header: mediumStyles.header,
  title: css`
    ${theme.typography.headings[4].xl}
  `,
  icon: css`
    width: 22px;
  `,
  body: mediumStyles.body,
  content: mediumStyles.content,
};

const accordionSizeStyles = {
  xs: smallStyles,
  sm: smallStyles,
  md: mediumStyles,
  lg: largeStyles,
};

const sizeStyles = (key: keyof sizeStyle, size: AccordionSize) =>
  breakpointMap(
    size,
    (accordionSize) => accordionSizeStyles[accordionSize][key]
  );

const Header = styled.div<{ size: AccordionSize }>`
  ${({ size }) => sizeStyles('header', size)};
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const Title = styled(Heading)<{ componentSize: AccordionSize }>`
  ${({ componentSize }) => sizeStyles('title', componentSize)};
`;

const IconStyles = css<{ size: AccordionSize }>`
  ${({ size }) => sizeStyles('icon', size)};
  fill: ${theme.colors.primary.graysGray};
`;

const ChevronDown = styled(ChevronDownIcon)`
  ${IconStyles}
`;

const ChevronUp = styled(ChevronUpIcon)`
  ${IconStyles}
`;

const Body = styled.div<{ size: AccordionSize }>`
  ${({ size }) => sizeStyles('body', size)};
`;

const Content = styled.div<{ size: AccordionSize }>`
  ${({ size }) => sizeStyles('content', size)};
  border-left: 4px solid ${theme.colors.system.systemGreen};
`;

const Label = styled(BodyText)`
  color: ${theme.colors.system.systemGreen};
  font-weight: 700;
  margin-bottom: ${theme.spacings[3].rem};
`;

const Answer = styled(BodyText)`
  color: ${theme.colors.neutrals.transparentGrays[2]};
`;

const Link = styled(LinkText)<{ componentSize: AccordionSize }>`
  display: block;
  margin-top: ${theme.spacings[4].rem};
  ${({ componentSize }) =>
    componentSize === 'lg' && `margin-bottom: ${theme.spacings[2].rem};`}
`;

export const AccordionQAndA: React.FC<AccordionQAndAProps> = ({
  answer,
  href,
  open,
  size,
  toggleOpen,
  title,
}) => (
  <BaseAccordion
    header={
      <Header size={size}>
        <Title componentSize={size} size={4}>
          {title}
        </Title>
        {open ? <ChevronUp size={size} /> : <ChevronDown size={size} />}
      </Header>
    }
    onToggleOpen={toggleOpen}
    open={open}
  >
    <Body size={size}>
      <Content size={size}>
        <Label>Answer</Label>
        <Answer>{answer}</Answer>
      </Content>
      {href && (
        <Link componentSize={size} href={href}>
          Read more
        </Link>
      )}
    </Body>
  </BaseAccordion>
);
